import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Save, Code, FileText, Grid3X3, Home } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import BlotFormatter from 'quill-blot-formatter';

// Register custom fonts with Quill
const Font = Quill.import('formats/font');
Font.whitelist = ['Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Trebuchet MS', 'Courier New'];
Quill.register(Font, true);

// Register blot formatter for image resizing
Quill.register('modules/blotFormatter', BlotFormatter);

// Custom fonts for the email editor
const FONTS = ['Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Trebuchet MS', 'Courier New'];

const TemplateForm = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditMode = !!templateId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [template, setTemplate] = useState({
    name: '',
    subject: '',
    body: '',
    default_attachments: []
  });
  
  // Refs for cursor position insertion
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const mentionDropdownRef = useRef(null);
  const [insertTarget, setInsertTarget] = useState('body'); // 'body' or 'subject'
  
  // @ mention autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionSource, setMentionSource] = useState('body'); // 'body' or 'subject'
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  // Table insertion modal state
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableConfig, setTableConfig] = useState({
    rows: 3,
    cols: 3,
    showBorders: true,
    columnWidths: ''
  });
  const [editingTable, setEditingTable] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'font': FONTS }],
        [{ 'header': [1, 2, 3, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image', 'insertTable'],
        ['clean']
      ],
      handlers: {
        insertTable: function() {}
      }
    },
    blotFormatter: {}
  }), []);

  const formats = [
    'font', 'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align', 'link', 'image',
    'width', 'height', 'style'
  ];

  // Insert table into editor
  // Insert table into editor
  const insertTable = () => {
    const { rows, cols, showBorders, columnWidths } = tableConfig;
    const widths = columnWidths.split(',').map(w => w.trim()).filter(Boolean);
    
    const borderStyle = showBorders ? '1px solid #cccccc' : 'none';
    let tableHtml = `<table style="border-collapse: collapse; width: 100%; border: ${borderStyle};" data-border="${showBorders ? '1' : '0'}">`;
    
    for (let r = 0; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        const width = widths[c] || '';
        const widthStyle = width ? ` width: ${width.includes('%') ? width : width + 'px'};` : '';
        tableHtml += `<td style="border: ${borderStyle}; padding: 8px;${widthStyle}">&nbsp;</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table>';
    
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const currentHtml = quill.root.innerHTML;
      
      if (editingTable) {
        quill.root.innerHTML = currentHtml.replace(editingTable, tableHtml);
        setEditingTable(null);
      } else {
        quill.root.innerHTML = currentHtml + tableHtml + '<p><br></p>';
      }
      
      setTemplate(prev => ({ ...prev, body: quill.root.innerHTML }));
    }
    
    setShowTableModal(false);
    setTableConfig({ rows: 3, cols: 3, showBorders: true, columnWidths: '' });
    toast.success(editingTable ? 'Table updated' : 'Table inserted');
  };

  // Toggle borders on existing table
  const toggleTableBorders = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>[\s\S]*?<\/table>/i);
    
    if (!tableMatch) {
      toast.error('No table found');
      return;
    }
    
    let tableHtml = tableMatch[0];
    const hasBorders = tableHtml.includes('data-border="1"');
    
    if (hasBorders) {
      tableHtml = tableHtml
        .replace(/data-border="1"/g, 'data-border="0"')
        .replace(/border:\s*1px solid #[a-fA-F0-9]+;?/g, 'border: none;')
        .replace(/border:\s*1px solid [^;]+;?/g, 'border: none;');
    } else {
      tableHtml = tableHtml
        .replace(/data-border="0"/g, 'data-border="1"')
        .replace(/border:\s*none;?/g, 'border: 1px solid #cccccc;');
    }
    
    quill.root.innerHTML = currentHtml.replace(tableMatch[0], tableHtml);
    setTemplate(prev => ({ ...prev, body: quill.root.innerHTML }));
    toast.success(hasBorders ? 'Borders removed' : 'Borders added');
  };

  // Add row to existing table
  const addTableRow = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>([\s\S]*?)<\/table>/i);
    
    if (!tableMatch) {
      toast.error('No table found');
      return;
    }
    
    const firstRowMatch = tableMatch[1].match(/<tr[^>]*>([\s\S]*?)<\/tr>/i);
    if (firstRowMatch) {
      const cellMatches = firstRowMatch[1].match(/<td[^>]*>[\s\S]*?<\/td>/gi);
      if (cellMatches) {
        const hasBorders = tableMatch[0].includes('data-border="1"');
        const borderStyle = hasBorders ? '1px solid #cccccc' : 'none';
        const newRow = '<tr>' + cellMatches.map(() => 
          `<td style="border: ${borderStyle}; padding: 8px;">&nbsp;</td>`
        ).join('') + '</tr>';
        
        const newTableContent = tableMatch[1] + newRow;
        const newTable = tableMatch[0].replace(tableMatch[1], newTableContent);
        quill.root.innerHTML = currentHtml.replace(tableMatch[0], newTable);
        setTemplate(prev => ({ ...prev, body: quill.root.innerHTML }));
        toast.success('Row added');
      }
    }
  };

  // Add column to existing table
  const addTableColumn = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>([\s\S]*?)<\/table>/i);
    
    if (!tableMatch) {
      toast.error('No table found');
      return;
    }
    
    const hasBorders = tableMatch[0].includes('data-border="1"');
    const borderStyle = hasBorders ? '1px solid #cccccc' : 'none';
    const newCell = `<td style="border: ${borderStyle}; padding: 8px;">&nbsp;</td>`;
    
    let newContent = tableMatch[1].replace(/<\/tr>/gi, newCell + '</tr>');
    const newTable = tableMatch[0].replace(tableMatch[1], newContent);
    quill.root.innerHTML = currentHtml.replace(tableMatch[0], newTable);
    setTemplate(prev => ({ ...prev, body: quill.root.innerHTML }));
    toast.success('Column added');
  };

  // Edit existing table
  const handleEditTable = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) {
      toast.error('Editor not ready');
      return;
    }
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>[\s\S]*?<\/table>/i);
    
    if (tableMatch) {
      setEditingTable(tableMatch[0]);
      const hasBorder = tableMatch[0].includes('data-border="1"');
      const rowMatches = tableMatch[0].match(/<tr/gi);
      const firstRowCols = tableMatch[0].match(/<tr[^>]*>[\s\S]*?<\/tr>/i);
      const colMatches = firstRowCols ? firstRowCols[0].match(/<td/gi) : null;
      
      setTableConfig({
        rows: rowMatches ? rowMatches.length : 3,
        cols: colMatches ? colMatches.length : 3,
        showBorders: hasBorder,
        columnWidths: ''
      });
      setShowTableModal(true);
    } else {
      toast.error('No table found');
    }
  };

  // Setup custom toolbar button for table
  useEffect(() => {
    // Need a small delay to ensure Quill toolbar is rendered
    const timer = setTimeout(() => {
      const toolbarButtons = document.querySelectorAll('.ql-insertTable');
      // Get the one in the template form (might be multiple if both pages are loaded)
      const toolbarButton = toolbarButtons[toolbarButtons.length - 1];
      if (toolbarButton && !toolbarButton.dataset.initialized) {
        toolbarButton.dataset.initialized = 'true';
        toolbarButton.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h4v4H7V7zm6 0h4v4h-4V7zm-6 6h4v4H7v-4zm6 0h4v4h-4v-4z"/></svg>';
        toolbarButton.title = 'Insert Table';
        toolbarButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowTableModal(true);
        });
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [loading]);
  
  // Filter placeholders based on mention filter
  const filteredPlaceholders = useMemo(() => {
    if (!mentionFilter) return placeholders;
    const filter = mentionFilter.toLowerCase();
    return placeholders.filter(p => 
      p.display_name?.toLowerCase().includes(filter) ||
      p.description?.toLowerCase().includes(filter) ||
      p.placeholder?.toLowerCase().includes(filter)
    );
  }, [placeholders, mentionFilter]);

  // Handle @ key in subject input
  const handleSubjectKeyDown = (e) => {
    if (showMentions && mentionSource === 'subject') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.min(prev + 1, filteredPlaceholders.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredPlaceholders.length > 0) {
        e.preventDefault();
        insertMentionInSubject(filteredPlaceholders[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    }
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setTemplate(prev => ({ ...prev, subject: value }));
    
    // Check for @ trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      const charBeforeAt = atIndex > 0 ? value[atIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || atIndex === 0) && !textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt);
        setMentionStartIndex(atIndex);
        setMentionSource('subject');
        setSelectedMentionIndex(0);
        
        const input = subjectRef.current;
        if (input) {
          const rect = input.getBoundingClientRect();
          setMentionPosition({ top: rect.bottom + 5, left: rect.left });
        }
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMentionInSubject = (placeholder) => {
    const currentSubject = template.subject;
    const before = currentSubject.substring(0, mentionStartIndex);
    const after = currentSubject.substring(subjectRef.current?.selectionStart || currentSubject.length);
    const newValue = before + placeholder.placeholder + after;
    setTemplate(prev => ({ ...prev, subject: newValue }));
    setShowMentions(false);
    
    setTimeout(() => {
      const input = subjectRef.current;
      if (input) {
        input.focus();
        const newPos = mentionStartIndex + placeholder.placeholder.length;
        input.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // Handle @ in Quill editor
  const handleQuillChange = (content, delta, source, editor) => {
    setTemplate(prev => ({ ...prev, body: content }));
    
    if (source !== 'user') return;
    
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const selection = quill.getSelection();
    if (!selection) return;
    
    const text = quill.getText(0, selection.index);
    const atIndex = text.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = text.substring(atIndex + 1);
      const charBeforeAt = atIndex > 0 ? text[atIndex - 1] : '\n';
      
      if ((charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) && !textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionFilter(textAfterAt);
        setMentionStartIndex(atIndex);
        setMentionSource('body');
        setSelectedMentionIndex(0);
        
        const bounds = quill.getBounds(selection.index);
        const editorRect = quill.container.getBoundingClientRect();
        setMentionPosition({ 
          top: editorRect.top + bounds.top + bounds.height + 5,
          left: editorRect.left + bounds.left
        });
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const handleQuillKeyDown = (e) => {
    if (showMentions && mentionSource === 'body') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.min(prev + 1, filteredPlaceholders.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredPlaceholders.length > 0) {
        e.preventDefault();
        insertMentionInBody(filteredPlaceholders[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    }
  };

  const insertMentionInBody = (placeholder) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const selection = quill.getSelection();
    if (!selection) return;
    
    const deleteLength = selection.index - mentionStartIndex;
    quill.deleteText(mentionStartIndex, deleteLength);
    quill.insertText(mentionStartIndex, placeholder.placeholder);
    quill.setSelection(mentionStartIndex + placeholder.placeholder.length);
    
    setShowMentions(false);
  };

  // Close mentions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(e.target)) {
        setShowMentions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPlaceholders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-templates/placeholders`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPlaceholders(data);
      }
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    }
  }, [API_URL]);

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/attachments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  }, [API_URL]);

  const fetchTemplate = useCallback(async () => {
    if (!templateId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/email-templates/${templateId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTemplate({
          name: data.name || '',
          subject: data.subject || '',
          body: data.body || '',
          default_attachments: data.default_attachments || []
        });
      } else {
        toast.error('Template not found');
        navigate('/admin/email-templates');
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [API_URL, templateId, navigate]);

  useEffect(() => {
    fetchPlaceholders();
    fetchAttachments();
    if (isEditMode) {
      fetchTemplate();
    }
  }, [isEditMode, fetchPlaceholders, fetchAttachments, fetchTemplate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!template.name || !template.subject || !template.body) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const url = isEditMode
        ? `${API_URL}/api/email-templates/${templateId}`
        : `${API_URL}/api/email-templates`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(template)
      });

      if (response.ok) {
        toast.success(isEditMode ? 'Template updated' : 'Template created');
        navigate('/admin/email-templates');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    if (insertTarget === 'subject') {
      // Insert into subject at cursor position
      const input = subjectRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentSubject = template.subject;
        const newValue = currentSubject.substring(0, start) + placeholder + currentSubject.substring(end);
        setTemplate(prev => ({ ...prev, subject: newValue }));
        // Restore cursor position after the inserted placeholder
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + placeholder.length, start + placeholder.length);
        }, 0);
      } else {
        setTemplate(prev => ({ ...prev, subject: prev.subject + placeholder }));
      }
    } else {
      // Insert into body at cursor position using Quill
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          quill.insertText(range.index, placeholder);
          quill.setSelection(range.index + placeholder.length);
        } else {
          // No selection, insert at end
          const length = quill.getLength();
          quill.insertText(length - 1, placeholder);
        }
      } else {
        setTemplate(prev => ({ ...prev, body: prev.body + placeholder }));
      }
    }
    toast.success(`Inserted ${placeholder}`);
  };

  const toggleAttachment = (attachmentId) => {
    setTemplate(prev => ({
      ...prev,
      default_attachments: prev.default_attachments.includes(attachmentId)
        ? prev.default_attachments.filter(id => id !== attachmentId)
        : [...prev.default_attachments, attachmentId]
    }));
  };

  // Group placeholders by category
  const customerPlaceholders = placeholders.filter(p => p.category === 'customer');
  const bookingPlaceholders = placeholders.filter(p => p.category === 'booking');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c9a55a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="template-form-page">
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/email-templates')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Back to Templates"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </div>
          <h1 className="text-2xl font-bold text-[#f5f3ed]">
            {isEditMode ? 'Edit Template' : 'New Template'}
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
                <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Template Details</h2>
                <p className="text-[#f5f3ed]/50 text-xs mb-4">Type @ to insert variables</p>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#f5f3ed]/70">Template Name *</Label>
                    <Input
                      value={template.name}
                      onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Booking Confirmation"
                      className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                      data-testid="template-name"
                    />
                  </div>
                  <div className="relative">
                    <Label className="text-[#f5f3ed]/70">Email Subject *</Label>
                    <Input
                      ref={subjectRef}
                      value={template.subject}
                      onChange={handleSubjectChange}
                      onKeyDown={handleSubjectKeyDown}
                      onFocus={() => setInsertTarget('subject')}
                      placeholder="e.g., Your Horsebox Booking Confirmation - {{BookingID}}"
                      className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                      data-testid="template-subject"
                    />
                    <p className="text-[#f5f3ed]/50 text-xs mt-1">Type @ to insert variables or use the sidebar</p>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
                <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Email Body *</h2>
                <div 
                  className="bg-white rounded-lg overflow-hidden"
                  onFocus={() => setInsertTarget('body')}
                  onClick={() => setInsertTarget('body')}
                  onKeyDown={handleQuillKeyDown}
                >
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={template.body}
                    onChange={handleQuillChange}
                    modules={modules}
                    formats={formats}
                    className="[&_.ql-container]:min-h-[300px] [&_.ql-container]:max-h-[500px] [&_.ql-container]:overflow-y-auto [&_.ql-editor]:min-h-[300px]"
                    data-testid="template-body"
                  />
                </div>
                {/* Table Tools */}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingTable(null);
                      setTableConfig({ rows: 3, cols: 3, showBorders: true, columnWidths: '' });
                      setShowTableModal(true);
                    }}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                  >
                    <Grid3X3 className="w-4 h-4 mr-2" />
                    Insert Table
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={toggleTableBorders}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                  >
                    Toggle Borders
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTableRow}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                  >
                    + Row
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTableColumn}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                  >
                    + Column
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEditTable}
                    className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                  >
                    Edit Table
                  </Button>
                </div>
              </div>

              {/* Default Attachments */}
              <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
                <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Default Attachments</h2>
                <p className="text-[#f5f3ed]/50 text-sm mb-4">
                  Select attachments to include by default when using this template
                </p>
                
                {attachments.length === 0 ? (
                  <p className="text-[#f5f3ed]/50 text-sm">
                    No attachments available. <a href="/admin/attachments" className="text-[#c9a55a] hover:underline">Upload some</a>
                  </p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map(attachment => (
                      <div
                        key={attachment.attachment_id}
                        className="flex items-center space-x-3 p-3 rounded-lg bg-[#1a1f1a]/50"
                      >
                        <Checkbox
                          id={`attach-${attachment.attachment_id}`}
                          checked={template.default_attachments.includes(attachment.attachment_id)}
                          onCheckedChange={() => toggleAttachment(attachment.attachment_id)}
                          className="border-[#c9a55a] data-[state=checked]:bg-[#c9a55a]"
                        />
                        <Label
                          htmlFor={`attach-${attachment.attachment_id}`}
                          className="text-[#f5f3ed] cursor-pointer flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4 text-[#c9a55a]" />
                          {attachment.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/email-templates')}
                  className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
                  data-testid="save-template-btn"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a1f1a]" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      {isEditMode ? 'Save Changes' : 'Create Template'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Placeholders Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <Code className="w-5 h-5 text-[#c9a55a]" />
                <h2 className="text-lg font-semibold text-[#f5f3ed]">Placeholders</h2>
              </div>
              <p className="text-[#f5f3ed]/50 text-sm mb-2">Click to insert into:</p>
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setInsertTarget('subject')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    insertTarget === 'subject' 
                      ? 'bg-[#c9a55a] text-[#1a1f1a]' 
                      : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/70 hover:bg-[#1a1f1a]/70'
                  }`}
                >
                  Subject
                </button>
                <button
                  type="button"
                  onClick={() => setInsertTarget('body')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    insertTarget === 'body' 
                      ? 'bg-[#c9a55a] text-[#1a1f1a]' 
                      : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/70 hover:bg-[#1a1f1a]/70'
                  }`}
                >
                  Body
                </button>
              </div>

              {/* Customer Placeholders */}
              <div className="mb-6">
                <h3 className="text-[#c9a55a] text-sm font-medium mb-2">Customer Fields</h3>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {customerPlaceholders.map(p => (
                    <button
                      key={p.field}
                      type="button"
                      onClick={() => insertPlaceholder(p.placeholder)}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#c9a55a]/20 rounded transition-colors group"
                      title={p.description}
                    >
                      <code className="text-[#c9a55a]">{p.placeholder}</code>
                      <span className="text-[#f5f3ed]/50 ml-2 group-hover:text-[#f5f3ed]/70">{p.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Booking Placeholders */}
              <div>
                <h3 className="text-blue-400 text-sm font-medium mb-2">Booking Fields</h3>
                <p className="text-[#f5f3ed]/40 text-xs mb-2">
                  Using these will require a booking context
                </p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {bookingPlaceholders.map(p => (
                    <button
                      key={p.field}
                      type="button"
                      onClick={() => insertPlaceholder(p.placeholder)}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-500/20 rounded transition-colors group"
                      title={p.description}
                    >
                      <code className="text-blue-400">{p.placeholder}</code>
                      <span className="text-[#f5f3ed]/50 ml-2 group-hover:text-[#f5f3ed]/70">{p.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* @ Mentions Dropdown */}
      {showMentions && filteredPlaceholders.length > 0 && (
        <div
          ref={mentionDropdownRef}
          className="fixed z-50 bg-[#1a1f1a] border border-[#2d5f3f] rounded-lg shadow-xl max-h-64 overflow-y-auto"
          style={{ top: mentionPosition.top, left: mentionPosition.left, minWidth: '250px' }}
        >
          {filteredPlaceholders.map((p, index) => (
            <button
              key={p.field}
              type="button"
              onClick={() => mentionSource === 'subject' ? insertMentionInSubject(p) : insertMentionInBody(p)}
              className={`w-full text-left px-3 py-2 flex flex-col hover:bg-[#2d5f3f]/50 transition-colors ${
                index === selectedMentionIndex ? 'bg-[#2d5f3f]/70' : ''
              }`}
            >
              <code className={`text-sm ${p.category === 'customer' ? 'text-[#c9a55a]' : 'text-blue-400'}`}>
                {p.placeholder}
              </code>
              <span className="text-[#f5f3ed]/50 text-xs">{p.description}</span>
            </button>
          ))}
        </div>
      )}

      {/* Table Insertion Modal */}
      <Dialog open={showTableModal} onOpenChange={setShowTableModal}>
        <DialogContent className="bg-[#1a1f1a] border-[#2d5f3f] text-[#f5f3ed]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-[#c9a55a]" />
              {editingTable ? 'Edit Table' : 'Insert Table'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">Rows</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={tableConfig.rows}
                  onChange={(e) => setTableConfig(prev => ({ ...prev, rows: parseInt(e.target.value) || 1 }))}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Columns</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={tableConfig.cols}
                  onChange={(e) => setTableConfig(prev => ({ ...prev, cols: parseInt(e.target.value) || 1 }))}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#f5f3ed]/70">Column Widths (optional)</Label>
              <Input
                value={tableConfig.columnWidths}
                onChange={(e) => setTableConfig(prev => ({ ...prev, columnWidths: e.target.value }))}
                placeholder="e.g., 100,200,150 or 30%,40%,30%"
                className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
              />
              <p className="text-[#f5f3ed]/50 text-xs mt-1">
                Comma-separated values in pixels or percentages
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="showBordersTemplate"
                checked={tableConfig.showBorders}
                onCheckedChange={(checked) => setTableConfig(prev => ({ ...prev, showBorders: checked }))}
                className="border-[#2d5f3f] data-[state=checked]:bg-[#c9a55a]"
              />
              <Label htmlFor="showBordersTemplate" className="text-[#f5f3ed]/70 cursor-pointer">
                Show gridlines/borders
              </Label>
            </div>
            
            {/* Preview */}
            <div className="mt-4">
              <Label className="text-[#f5f3ed]/70 mb-2 block">Preview</Label>
              <div className="bg-white rounded p-4 overflow-x-auto">
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    {Array.from({ length: Math.min(tableConfig.rows, 5) }).map((_, r) => (
                      <tr key={r}>
                        {Array.from({ length: Math.min(tableConfig.cols, 5) }).map((_, c) => {
                          const widths = tableConfig.columnWidths.split(',').map(w => w.trim()).filter(Boolean);
                          const width = widths[c] || '';
                          const widthStyle = width ? { width: width.includes('%') ? width : `${width}px` } : {};
                          return (
                            <td
                              key={c}
                              style={{
                                border: tableConfig.showBorders ? '1px solid #ccc' : 'none',
                                padding: '8px',
                                backgroundColor: '#f9f9f9',
                                ...widthStyle
                              }}
                            >
                              &nbsp;
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(tableConfig.rows > 5 || tableConfig.cols > 5) && (
                  <p className="text-gray-500 text-xs mt-2">Preview limited to 5x5</p>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTableModal(false)}
              className="border-[#2d5f3f] text-[#f5f3ed]"
            >
              Cancel
            </Button>
            <Button
              onClick={insertTable}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
            >
              Insert Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateForm;
